<script setup>
// One membership type or pass as a joiner sees it (AppClub): name, a Pass chip
// (D31), then "From £22 a month · 18 and over · the club approves".
import {computed} from 'vue';
import {DISPLAY_AS} from '~/utils/memberships';
import {isGroupType} from '~/utils/membershipGroups';

const props = defineProps({
  type: {type: Object, required: true},
  to: {type: [Object, String], default: null},
  /** A one-line extra on the right ("Yours", "Applied"). */
  tag: {type: String, default: null},
});

const isPass = computed(() => props.type.displayAs === DISPLAY_AS.pass);
const lowerFirst = (text) => text ? text.charAt(0).toLowerCase() + text.slice(1) : text;
// D42: a group type says who it covers ("for 1 to 2 Adults (26 and over) and up to 6 Children (under 26)") instead of an age band.
const whoText = computed(() => {
  if (isGroupType(props.type) && props.type.whoItCovers) return lowerFirst(props.type.whoItCovers);
  return props.type.whoCanJoin && props.type.whoCanJoin !== 'Anyone' ? lowerFirst(props.type.whoCanJoin) : (props.type.whoCanJoin ? 'anyone' : null);
});
// D44/D45: introductory and linked-to-a-child types say so; D43 conditions are named so nobody is surprised on apply.
const extra = computed(() => [props.type.newToClubLine, props.type.renewsAsLine, props.type.needsChildLine].filter(Boolean).join(' · '));
const conditionsLine = computed(() => (props.type.conditions ?? []).length
    ? `You confirm: ${(props.type.conditions ?? []).map(c => String(c.text ?? '').replace(/\.\s*$/, '')).join('; ')}` : null);
const line = computed(() => [
  props.type.priceSummary,
  // R6 Low 8: "£30 for the pass" already says it; never "… for the pass · the pass".
  props.type.lengthSummary && isPass.value && !String(props.type.priceSummary ?? '').toLowerCase().includes(String(props.type.lengthSummary).toLowerCase()) ? props.type.lengthSummary : null,
  whoText.value,
  props.type.requiresApproval ? 'the club approves' : null,
].filter(Boolean).join(' · '));
</script>

<template>
  <component :is="to ? 'router-link' : 'div'" :to="to" class="mtr">
    <div class="mtr-ic" :class="{'mtr-ic--pass': isPass}">
      <v-icon size="20" :icon="isPass ? 'mdi-ticket-confirmation-outline' : 'mdi-card-account-details-outline'"/>
    </div>
    <div class="mtr-body">
      <div class="mtr-title">
        {{ type.name }}
        <span v-if="isPass" class="tl-chip tl-chip--primary mtr-chip">Pass</span>
        <span v-if="tag" class="tl-chip tl-chip--ok mtr-chip">{{ tag }}</span>
      </div>
      <div class="mtr-sub">{{ line }}</div>
      <div v-if="extra" class="mtr-sub">{{ extra }}</div>
      <div v-if="conditionsLine" class="mtr-sub">{{ conditionsLine }}</div>
    </div>
    <v-icon v-if="to" size="18" icon="mdi-chevron-right" class="mtr-chev"/>
  </component>
</template>

<style scoped lang="scss">
.mtr {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  color: inherit;
  text-decoration: none;
  border-top: 1px solid rgb(var(--v-theme-borderLight));

  &:first-child { border-top: 0; }
  &:is(a):hover { background: rgb(var(--v-theme-light)); }
}
.mtr-ic {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &--pass { background: rgb(var(--v-theme-successTint)); color: rgb(var(--v-theme-success)); }
}
.mtr-body { flex: 1; min-width: 0; }
.mtr-title { font-size: 14px; font-weight: 700; letter-spacing: -0.2px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.mtr-chip { font-size: 10.5px; }
.mtr-sub { font-size: 12px; color: rgb(var(--v-theme-textSubtle)); margin-top: 2px; line-height: 1.4; }
.mtr-chev { color: rgb(var(--v-theme-textSubtle)); }
</style>
